import { useEffect } from "react";
import { useParams, Link, Navigate } from "react-router-dom";
import { motion } from "framer-motion";
import { ExternalLink, ArrowUpRight, Users, Globe2 } from "lucide-react";
import {
  EDITORIAL_BOARDS,
  countMembers,
  uniqueCountries,
  editorSlug,
} from "@/data/editorialBoards";
import PageHeader from "@/components/landing/PageHeader";
import Footer from "@/components/landing/Footer";
import Navbar from "@/components/landing/Navbar";
import SEO from "@/components/SEO";

const BASE_TITLE =
  "International Sci Ink Press — Advancing Medical Knowledge Through Open Science";

export default function EditorialBoardPage() {
  const { journalId } = useParams();
  const board = EDITORIAL_BOARDS[journalId];

  useEffect(() => {
    if (board) {
      document.title = `${board.journalShort} — Editorial Board · International Sci Ink Press`;
    }
    return () => {
      document.title = BASE_TITLE;
    };
  }, [board]);

  if (!board) {
    return <Navigate to="/" replace />;
  }

  // Person JSON-LD for every board member — strong SEO signal for Google Scholar
  const allMembers = board.groups.flatMap((g) => g.members.map((m) => ({ ...m, role: g.role })));
  const personLd = {
    "@context": "https://schema.org",
    "@type": "ItemList",
    name: `${board.journalShort} Editorial Board`,
    numberOfItems: allMembers.length,
    itemListElement: allMembers.map((m, i) => ({
      "@type": "ListItem",
      position: i + 1,
      item: {
        "@type": "Person",
        name: m.name,
        jobTitle: m.role,
        affiliation: { "@type": "Organization", name: m.affiliation },
        nationality: m.country,
        ...(m.orcid && { sameAs: [`https://orcid.org/${m.orcid}`] }),
      },
    })),
  };

  const periodicalLd = {
    "@context": "https://schema.org",
    "@type": "Periodical",
    name: board.journalTitle,
    alternateName: board.journalShort,
    issn: board.issn.replace(/^[^:]+:\s*/, "").split(" · ")[0],
    url: board.journalUrl,
    publisher: {
      "@type": "Organization",
      name: "International Sci Ink Press Ltd",
      url: "https://journalmedica.com",
    },
  };

  const total = countMembers(journalId);
  const countries = uniqueCountries(journalId);

  return (
    <div className="bg-[var(--sci-bg)]">
      <SEO
        title={`${board.journalShort} Editorial Board`}
        description={`The editorial board of ${board.journalTitle} (${board.issn}) — ${allMembers.length} international clinicians and researchers from ${uniqueCountries(journalId).length} countries.`}
        path={`/editorial-board/${journalId}`}
        breadcrumbs={[
          { name: "Home", url: "/" },
          { name: "Journals", url: "/#journals" },
          { name: `${board.journalShort} Board`, url: `/editorial-board/${journalId}` },
        ]}
        jsonLd={[periodicalLd, personLd]}
      />
      <Navbar />
      <PageHeader
        overline={`Editorial Board · ${board.journalShort}`}
        title={
          <>
            {board.journalShort} <br />
            <em className="not-italic text-[var(--sci-teal)]">Editorial Board.</em>
          </>
        }
        sub={`${board.journalTitle} · ${board.issn}`}
      />

      {/* Stats strip */}
      <section className="bg-white border-b border-[var(--sci-border)]">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-2 md:grid-cols-4 divide-x divide-[var(--sci-border)]">
            <Stat label="Board members" value={total} />
            <Stat label="Countries" value={countries.length} />
            <Stat label="Specialty sections" value={board.groups.length} />
            <Stat
              label="Submission portal"
              value="eJManager"
              href={board.submitUrl}
              mono
            />
          </div>
        </div>
      </section>

      {/* Body */}
      <section className="py-20 md:py-24">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid lg:grid-cols-12 gap-12 lg:gap-16">
            {/* TOC */}
            <aside className="lg:col-span-3 lg:sticky lg:top-28 self-start">
              <div className="overline text-[var(--sci-text-muted)] mb-4">
                Index
              </div>
              <ul className="space-y-2.5">
                {board.groups.map((g, i) => (
                  <li key={g.role}>
                    <a
                      href={`#g-${i}`}
                      data-testid={`board-toc-${i}`}
                      className="group inline-flex items-start gap-2 text-sm text-[var(--sci-navy)] hover:text-[var(--sci-teal)] transition-colors"
                    >
                      <span className="font-mono-sci text-[10px] tracking-wider text-[var(--sci-text-muted)] group-hover:text-[var(--sci-teal)] mt-1 shrink-0">
                        {String(i + 1).padStart(2, "0")}
                      </span>
                      <span className="leading-snug">
                        {g.role}{" "}
                        <span className="text-[var(--sci-text-muted)] text-xs">
                          ({g.members.length})
                        </span>
                      </span>
                    </a>
                  </li>
                ))}
              </ul>

              <div className="mt-10 space-y-2 text-xs">
                <a
                  href={board.sourceUrl}
                  target="_blank"
                  rel="noopener noreferrer"
                  data-testid="board-source-link"
                  className="inline-flex items-center gap-2 text-[var(--sci-navy)] hover:text-[var(--sci-teal)] underline decoration-dotted"
                >
                  Source · journal page
                  <ExternalLink size={11} />
                </a>
                <div className="block">
                  <Link
                    to={`/editorial-board/${journalId === "ijmrcr" ? "ijsm" : "ijmrcr"}`}
                    data-testid="board-other-link"
                    className="inline-flex items-center gap-2 text-[var(--sci-navy)] hover:text-[var(--sci-teal)]"
                  >
                    Other board ·{" "}
                    {journalId === "ijmrcr" ? "IJSM" : "IJMRCR"}
                    <ArrowUpRight size={12} />
                  </Link>
                </div>
              </div>
            </aside>

            {/* Main */}
            <div className="lg:col-span-9 space-y-16 md:space-y-20">
              {board.groups.map((g, i) => (
                <motion.section
                  key={g.role}
                  id={`g-${i}`}
                  data-testid={`board-group-${i}`}
                  initial={{ opacity: 0, y: 24 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true, amount: 0.2 }}
                  transition={{ duration: 0.55 }}
                  className="scroll-mt-28"
                >
                  <div className="flex items-end justify-between gap-4 mb-8 border-b border-[var(--sci-border)] pb-4">
                    <div>
                      <div className="overline text-[var(--sci-teal)]">
                        Section {String(i + 1).padStart(2, "0")}
                      </div>
                      <h2 className="font-serif-display text-2xl md:text-3xl lg:text-4xl text-[var(--sci-navy)] tracking-tight leading-tight mt-2">
                        {g.role}
                      </h2>
                    </div>
                    <div className="font-mono-sci text-xs tracking-wider text-[var(--sci-text-muted)] shrink-0">
                      {g.members.length} {g.members.length === 1 ? "member" : "members"}
                    </div>
                  </div>

                  <div className="grid sm:grid-cols-2 gap-4 md:gap-5">
                    {g.members.map((m, j) => (
                      <MemberCard
                        key={m.name + j}
                        member={m}
                        testid={`board-member-${i}-${j}`}
                      />
                    ))}
                  </div>
                </motion.section>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* CTA bottom */}
      <section className="py-20 md:py-24 bg-[var(--sci-navy)] text-white">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <div className="overline text-white/65 mb-4">
            Ready to submit?
          </div>
          <h3 className="font-serif-display text-3xl md:text-5xl tracking-tight leading-tight">
            Submit your manuscript to <em className="not-italic text-[var(--sci-teal)]">{board.journalShort}</em>.
          </h3>
          <div className="mt-8 flex flex-wrap gap-3 justify-center">
            <a
              href={board.submitUrl}
              target="_blank"
              rel="noopener noreferrer"
              data-testid="board-submit-cta"
              className="btn-on-dark inline-flex items-center gap-2 px-7 py-3.5 text-sm tracking-wide"
            >
              Open submission portal
              <ArrowUpRight size={16} />
            </a>
            <Link
              to="/#standards"
              data-testid="board-standards-cta"
              className="inline-flex items-center gap-2 px-7 py-3.5 text-sm tracking-wide border border-white/40 hover:border-white text-white"
            >
              Read editorial standards
            </Link>
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
}

function Stat({ label, value, href, mono }) {
  const Wrapper = href ? "a" : "div";
  const wrapperProps = href
    ? { href, target: "_blank", rel: "noopener noreferrer" }
    : {};
  return (
    <Wrapper
      {...wrapperProps}
      className="px-6 py-7 md:px-8 md:py-9 block group"
    >
      <div className="overline text-[var(--sci-text-muted)] mb-3 flex items-center gap-2">
        {label === "Countries" && (
          <Globe2 size={12} className="text-[var(--sci-teal)]" />
        )}
        {label === "Board members" && (
          <Users size={12} className="text-[var(--sci-teal)]" />
        )}
        {label}
      </div>
      <div
        className={`text-[var(--sci-navy)] leading-none group-hover:text-[var(--sci-teal)] transition-colors ${
          mono
            ? "font-mono-sci text-lg tracking-wider"
            : "font-serif-display text-4xl md:text-5xl"
        }`}
      >
        {value}
      </div>
      {href && (
        <div className="mt-3 inline-flex items-center gap-1 text-[10px] font-mono-sci tracking-wider text-[var(--sci-text-muted)] group-hover:text-[var(--sci-teal)]">
          OPEN PORTAL <ExternalLink size={10} />
        </div>
      )}
    </Wrapper>
  );
}

function MemberCard({ member, testid }) {
  const initials = member.name
    .split(" ")
    .map((p) => p[0])
    .filter(Boolean)
    .slice(0, 2)
    .join("");
  const slug = editorSlug(member.name);
  return (
    <Link
      to={`/editor/${slug}`}
      data-testid={testid}
      className="group flex items-start gap-4 bg-white border border-[var(--sci-border)] p-5 md:p-6 hover:border-[var(--sci-teal)] transition-colors"
    >
      <div className="w-12 h-12 shrink-0 bg-[var(--sci-bg)] border border-[var(--sci-border)] flex items-center justify-center font-serif-display text-lg text-[var(--sci-navy)] group-hover:bg-[var(--sci-navy)] group-hover:text-white transition-colors">
        {initials || "·"}
      </div>
      <div className="min-w-0 flex-1">
        <div className="font-serif-display text-base md:text-lg text-[var(--sci-navy)] leading-tight group-hover:text-[var(--sci-teal)] transition-colors">
          {member.name}
        </div>
        <div className="text-xs text-[var(--sci-text-muted)] mt-1.5 leading-relaxed">
          {member.affiliation}
        </div>
        <div className="mt-2.5 flex flex-wrap items-center gap-3 text-[11px] font-mono-sci tracking-wider">
          <span className="text-[var(--sci-teal)]">{member.country}</span>
          {member.orcid && (
            <span className="text-[var(--sci-text-muted)]">
              ORCID · {member.orcid}
            </span>
          )}
          <span className="ml-auto inline-flex items-center gap-1 text-[var(--sci-text-muted)] group-hover:text-[var(--sci-teal)]">
            Profile
            <ArrowUpRight size={11} />
          </span>
        </div>
      </div>
    </Link>
  );
}
